/** @jsxRuntime classic */
/** @jsx jsx */
import {FC, useEffect, useRef, useState} from 'react';
import {css, jsx} from '@emotion/react';
import {Icon, InfoBox, Tabs} from '@flexe/ui-components';
import ContainersService from '../../shared/services/ContainersService';
import {ApiResponse, Reservation} from '../../shared/CommonInterfaces';
import WarehouseService from '../../shared/services/WarehouseService';
import InboundService from '../../shared/services/InboundService';
import {usePrevious, valueIsNotUndefined} from '../../shared/ReactHelpers';
import PaginationButtons from '../../shared/PaginationButtons';
import PackingListService from '../../shared/services/PackingListService';
import CompleteInbound from '../warehouse/CompleteInbound';
import {
  GetInboundLpnContentsFilters,
  GetInboundsFilters,
  Inbound,
  InboundLpn,
  InboundLpnItem,
  InboundState,
  SkuContent
} from './InboundInterfaces';
import {LpnContentRow} from './InboundSummary/LpnContentRow';
import {SkuContentRow} from './InboundSummary/SkuContentRow';
import PoContentRow, {PoContentRowProps} from './InboundSummary/PoContentRow';

enum InboundSummaryTabKeys {
  SKU = 'skus',
  LPN = 'lpns',
  PO = 'purchaseOrders'
}

interface Props {
  containersService: ContainersService;
  warehouseService: WarehouseService;
  inboundService: InboundService;
  packingListService: PackingListService;
  reservation: Reservation;
  lpnReceiveOnly: boolean;
  inboundState: InboundState;
  allowLpnTransferInAllInboundStates?: boolean;
  showEditLpnForAsn?: boolean;
  inbound: Inbound;
  warehouseProps?: {
    isBolUploaded?: boolean;
    isInboundPackagingSet: boolean;
    handleInboundCompletionErrors(errors: string[], response);
    reloadContainer();
    warehouseId: number;
  };
  shipperProps?: {
    shipperCompanyId: number;
    isShipper: boolean;
    handleRefreshPage: () => void;
    handleApiError: (errTitle: string[], response: ApiResponse<any>) => void;
  };
}

const DEFAULT_LPN_PAGE_SIZE = 50;
const DEFAULT_PO_PAGE_SIZE = 50;

export const InboundSummary: FC<Props> = (props) => {
  const {
    reservation,
    inboundState,
    inboundService,
    containersService,
    packingListService,
    inbound,
    warehouseProps,
    shipperProps: {isShipper, handleRefreshPage} = {}
  } = props;
  const [selectedTab, setSelectedTab] = useState(InboundSummaryTabKeys.SKU);

  // LPN endpoint response
  const [currentLpnPage, setCurrentLpnPage] = useState<number>(1);
  const [lpnContinuationTokens, setLpnContinuationTokens] = useState({1: null});
  const [totalLpnsCount, setTotalLpnsCount] = useState<number>(0);
  const [lpnContents, setLpnContents] = useState<InboundLpn[]>(null);
  const [lpnFetchErrors, setLpnFetchErrors] = useState<string[]>([]);
  const [showLpnUoms, setShowLpnUoms] = useState<boolean>(false);

  const [poInboundContents, setPoInboundContents] = useState<Inbound[]>(null);
  const [poFetchErrors, setPoFetchErrors] = useState<string[]>([]);
  const [currentPoPage, setCurrentPoPage] = useState<number>(1);
  const [poContinuationTokens, setPoContinuationTokens] = useState({1: null});
  const [totalPoCount, setTotalPoCount] = useState<number>(0);

  // SKU endpoint response
  const [skuContents, setSkuContents] = useState<SkuContent[]>(null);
  const [skuFetchErrors, setSkuFetchErrors] = useState<string[]>([]);
  const [showSkuUoms, setShowSkuUoms] = useState(false);
  const [showQuantityWarning, setShowQuantityWarning] = useState(false);

  const [inProgressContainerIds, setInProgressContainerIds] = useState<number[]>();
  const [fetchInProgressContainerError, setFetchInProgressContainerError] = useState<string[]>([]);

  useEffect(() => {
    fetchSkuData();
    fetchLpnData();
    fetchPoInboundData();
  }, [inbound]);

  useEffect(() => {
    fetchInProgressContainerIds();
  }, []);

  const prevPage = usePrevious(currentLpnPage);
  useEffect(() => {
    if (valueIsNotUndefined(prevPage)) {
      fetchLpnData();
    }
  }, [currentLpnPage]);

  const prevPoPage = usePrevious(currentPoPage);
  useEffect(() => {
    if (valueIsNotUndefined(prevPoPage)) {
      fetchPoInboundData();
    }
  }, [currentPoPage]);

  const fetchInProgressContainerIds = async () => {
    const inProgressInboundFilters: GetInboundsFilters = {
      'warehouse-id': props.warehouseProps.warehouseId,
      'reservation-id[]': [props.reservation.id],
      'state[]': ['confirmed', 'arrived', 'receiving']
    };
    const response = await inboundService.getInbounds(inProgressInboundFilters);
    const responseInProgressContainerIds = response?.data?.inbounds
      .filter((ib) => ib.actualArrivalTime !== null)
      .map((ib) => ib.id);
    if (!response?.errors) {
      setInProgressContainerIds(responseInProgressContainerIds);
    } else {
      setFetchInProgressContainerError(response.errors.map((error) => error.detail));
    }
  };

  const fetchSkuData = async () => {
    setSkuFetchErrors([]);

    try {
      const skuResponse = await inboundService.getInboundSkuContentsById(inbound.id);
      if (skuResponse.data) {
        const contents = skuResponse.data.skus || [];
        setSkuContents(contents);
        setShowSkuUoms(shouldDisplayUomsInSkuTab(contents));
      } else if (skuResponse?.errors?.length > 0) {
        setSkuFetchErrors(skuResponse.errors.map((error) => error.detail));
        setSkuContents([]);
        setShowSkuUoms(false);
      }
    } catch (error) {
      setSkuContents([]);
      setSkuFetchErrors([error.toString()]);
      setShowSkuUoms(false);
    }
  };

  const fetchLpnData = async () => {
    setLpnFetchErrors([]);
    try {
      const filter = buildFetchLpnRequestFilters();
      const lpnResponse = await inboundService.getInboundLpnContentsById(inbound.id, filter);
      if (lpnResponse.data) {
        const contents = lpnResponse.data.lpns || [];
        setLpnContents(contents);
        const newContinuationTokens = {...lpnContinuationTokens};
        newContinuationTokens[currentLpnPage + 1] = lpnResponse.data.continuationToken;
        setLpnContinuationTokens(newContinuationTokens);
        setTotalLpnsCount(lpnResponse.data.totalLpns);
        setShowLpnUoms(shouldDisplayUomsInLpnTab(contents));
      } else if (lpnResponse?.errors?.length > 0) {
        setLpnFetchErrors(lpnResponse.errors.map((error) => error.detail));
        setLpnContents([]);
        setTotalLpnsCount(0);
        setShowLpnUoms(false);
      }
    } catch (error) {
      setLpnFetchErrors([error.toString()]);
      setLpnContents([]);
      setTotalLpnsCount(0);
      setShowLpnUoms(false);
    }
  };

  const fetchPoInboundData = async () => {
    setPoFetchErrors([]);
    try {
      const filter = buildFetchPoRequestFilters();
      const poResponse = await inboundService.getInbounds(filter);
      if (poResponse.data) {
        const contents = poResponse.data.inbounds || [];
        setPoInboundContents(contents);
        const newContinuationTokens = {...poContinuationTokens};
        newContinuationTokens[currentPoPage + 1] = poResponse.data.continuationToken;
        setPoContinuationTokens(newContinuationTokens);
        setTotalPoCount(poResponse.data.totalCount);
      } else if (poResponse?.errors?.length > 0) {
        setPoFetchErrors(poResponse.errors.map((error) => error.detail));
        setPoInboundContents([]);
        setTotalPoCount(0);
      }
    } catch (error) {
      setPoInboundContents([]);
      setPoFetchErrors([error.toString()]);
      setTotalPoCount(0);
    }
  };

  const buildFetchPoRequestFilters = (): GetInboundsFilters => {
    const token = poContinuationTokens[currentPoPage];

    return {
      'warehouse-id': props.warehouseProps.warehouseId,
      'purchase-order[]': props.inbound.purchaseOrders,
      'continuation-token': token,
      'page-size': DEFAULT_PO_PAGE_SIZE
    };
  };

  const buildFetchLpnRequestFilters = (): GetInboundLpnContentsFilters => {
    const token = lpnContinuationTokens[currentLpnPage];

    return {
      continuationToken: token,
      pageSize: DEFAULT_LPN_PAGE_SIZE
    };
  };

  const onTabSelection = (key: InboundSummaryTabKeys) => {
    if (key === selectedTab) {
      return;
    }
    setSelectedTab(key);
  };

  const getTabs = () => {
    const baseTabs = [
      {
        active: selectedTab === InboundSummaryTabKeys.SKU,
        key: InboundSummaryTabKeys.SKU,
        title: 'SKUs'
      },
      {
        active: selectedTab === InboundSummaryTabKeys.LPN,
        key: InboundSummaryTabKeys.LPN,
        title: 'LPNs'
      },
      {
        active: selectedTab === InboundSummaryTabKeys.PO,
        key: InboundSummaryTabKeys.PO,
        title: 'Purchase Orders'
      }
    ];

    return baseTabs;
  };

  const renderTableHeader = () => {
    switch (selectedTab) {
      case InboundSummaryTabKeys.SKU:
        return (
          <div className="row headers">
            <div className="col-md-2">SKU</div>
            <div className="col-md-2">Description</div>
            <div className="col-md-2">PO#</div>
            {showSkuUoms && <div className="col-md-1">Expected Inner/Outer UoM</div>}
            {showSkuUoms && <div className="col-md-1">Received Inner/Outer UoM</div>}
            <div className="col-md-1">Expected Qty/UoM</div>
            <div className="col-md-2">Received Qty/UoM</div>
            <div className="col-md-1">Damaged Received</div>
            {!isShipper && <div className="col-md-1">SKU Labels</div>}
          </div>
        );
      case InboundSummaryTabKeys.LPN:
        return (
          <div>
            <div
              css={css`
                display: flex;
                justify-content: flex-end;
                margin: 5px;
              `}
            >
              <PaginationButtons
                count={totalLpnsCount}
                currentPage={currentLpnPage}
                pageSize={DEFAULT_LPN_PAGE_SIZE}
                setCurrentPage={setCurrentLpnPage}
              />
            </div>
            <div className="row headers">
              <div className="col-md-1">LPN</div>
              <div className="col-md-2">SKU</div>
              <div className="col-md-2">PO#</div>
              {showLpnUoms && <div className="col-md-1">Received Inner/Outer UoM</div>}
              {isAsnLpns() && <div className="col-md-1">Expected Qty</div>}
              <div className="col-md-1">Received Qty</div>
              <div className="col-md-1">Received Date</div>
              <div className="col-md-2">Custom Parameters</div>
              <div className="col-md-1">Damaged?</div>
            </div>
          </div>
        );
      case InboundSummaryTabKeys.PO:
        return (
          <div>
            <div
              css={css`
                display: flex;
                justify-content: flex-end;
                margin: 5px;
              `}
            >
              <PaginationButtons
                count={totalPoCount}
                currentPage={currentPoPage}
                pageSize={DEFAULT_PO_PAGE_SIZE}
                setCurrentPage={setCurrentPoPage}
              />
            </div>
            <div className="row headers">
              <div className="col-md-2">PO#</div>
              <div className="col-md-2">Inbound Deliveries</div>
              <div className="col-md-2">Reservations</div>
            </div>
          </div>
        );
    }
  };

  const shouldDisplayUomsInSkuTab = (contents: SkuContent[]) => {
    if (!contents || contents.length === 0) return false;

    return contents.some(
      (skuContent: SkuContent) =>
        skuContent.expectedQuantity?.uoms?.eachesPerInnerPack && skuContent.expectedQuantity?.uoms?.eachesPerOuterCase
    );
  };

  const shouldDisplayUomsInLpnTab = (contents: InboundLpn[]) => {
    if (!contents || contents.length === 0) return false;

    return contents.some((lpn: InboundLpn) =>
      lpn.items.some((item: InboundLpnItem) => item.uoms?.eachesPerInnerPack && item.uoms?.eachesPerOuterCase)
    );
  };

  const isAsnLpns = () => {
    if (!lpnContents || lpnContents.length === 0) return false;

    return lpnContents.every((lpn: InboundLpn) =>
      lpn.items.every((item: InboundLpnItem) => item.expectedQuantity != null)
    );
  };

  const renderTableBody = () => {
    switch (selectedTab) {
      case InboundSummaryTabKeys.SKU:
        return renderSkuTabData();
      case InboundSummaryTabKeys.LPN: {
        return renderLpnTabData();
      }
      case InboundSummaryTabKeys.PO: {
        return renderPoTabData();
      }
    }
    return null;
  };

  const renderPoTabData = () => {
    if (!poInboundContents) return <Icon icon="spinner" size="lg" animation="spin" label="Fetching data" />;
    if (poInboundContents.length === 0) return <div className="alert alert-danger">No POs found for this inbound.</div>;

    // Build poTabDatas by grouping inbounds by purchase order
    const poTabDatas: PoContentRowProps[] = props.inbound.purchaseOrders.map((purchaseOrder) => {
      // Find all inbounds that include this purchase order
      const matchingInbounds = poInboundContents.filter((matchInbound) =>
        matchInbound.purchaseOrders.includes(purchaseOrder)
      );

      // Extract inbound IDs and reservation names
      const inboundIds = matchingInbounds.map((matchInbound) => matchInbound.id);
      const reservationNames = matchingInbounds.map(
        (matchInbound) => `#${matchInbound.reservation.id}: ${matchInbound.reservation.shipperName}`
      );

      return {
        data: {
          purchaseOrder,
          inboundIds,
          reservationNames
        },
        isShipper: !!props.shipperProps?.isShipper
      };
    });

    return (
      <div>
        {poTabDatas.map((poData) => (
          <PoContentRow key={poData.data.purchaseOrder} data={poData.data} isShipper={poData.isShipper} />
        ))}
      </div>
    );
  };

  const renderLpnTabData = () => {
    if (!lpnContents) return <Icon icon="spinner" size="lg" animation="spin" label="Fetching data" />;
    if (lpnContents.length === 0) return <div className="alert alert-danger">No LPNs found for this inbound.</div>;

    const unCorrectableInboundStates = [InboundState.Completed, InboundState.Cancelled];
    let enableEdits = false;

    // Whether inbounds can be edited depends on reservation configuration and the inbound state
    // Configuration overrides all ASN LPNs can be edited only if FF enabled and inbound state is new or confirmed
    // otherwise, edits are allowed for all states except cancelled and completed
    if (props.allowLpnTransferInAllInboundStates) {
      enableEdits = !isShipper;
    } else if (isAsnLpns()) {
      if (props.showEditLpnForAsn) {
        enableEdits = !unCorrectableInboundStates.includes(inbound.state as InboundState) && !isShipper;
      } else {
        enableEdits = false;
      }
    } else {
      enableEdits = !unCorrectableInboundStates.includes(inbound.state as InboundState) && !isShipper;
    }

    return (
      <div>
        {lpnContents.map((lpn: InboundLpn) =>
          lpn.items.map((item: InboundLpnItem, itemIndex: number) => (
            <LpnContentRow
              key={`${lpn.id}-${item.id}`}
              lpn={lpn}
              item={item}
              inbound={inbound}
              reservationId={reservation.id}
              shouldDisplayLpn={itemIndex === 0}
              shouldDisplayUoms={showLpnUoms}
              isAsnLpns={isAsnLpns()}
              enableEdits={enableEdits}
              isShipper={isShipper}
              lpnReceiveOnly={props.lpnReceiveOnly}
              inProgressContainerIds={inProgressContainerIds}
              onActionComplete={() => fetchLpnData()}
            />
          ))
        )}
      </div>
    );
  };

  const renderSkuTabData = () => {
    if (skuFetchErrors.length > 0) {
      return null; // Errors are displayed above the table
    }
    if (!skuContents) {
      return <Icon icon="spinner" size="lg" animation="spin" label="Fetching data" />;
    }
    if (skuContents.length === 0) {
      return <div className="alert alert-info">No SKUs found for this inbound.</div>;
    }

    return skuContents.map((skuContent) => {
      return (
        <SkuContentRow
          key={`${inbound.id}-${skuContent.item.inventoryId}-${skuContent.purchaseOrder}`}
          content={skuContent}
          inboundId={inbound.id}
          isShipper={!!isShipper}
          inboundState={inboundState}
          showUoms={showSkuUoms}
          shipperProps={{
            containersService,
            packingListService,
            handleRefreshPage
          }}
          showQuantityCallback={() => setShowQuantityWarning(true)}
        />
      );
    });
  };

  const errorDisplay = [...skuFetchErrors, ...lpnFetchErrors, ...fetchInProgressContainerError, ...poFetchErrors].map(
    (error, idx) => {
      return (
        <div className="alert alert-danger" role="alert" key={`error:${idx}`}>
          {error}
        </div>
      );
    }
  );

  const renderTable = () => (
    <div className="lpn-contents-component">
      {errorDisplay}

      <div className="table">
        {renderTableHeader()}
        {renderTableBody()}
      </div>
    </div>
  );

  const isContainerDeliveryActive = () => {
    return [InboundState.New, InboundState.Altered, InboundState.Confirmed].includes(inbound.state as InboundState);
  };

  const isContainerDeliveryInProgress = () => {
    return [InboundState.Confirmed, InboundState.Arrived, InboundState.Receiving].includes(
      inbound.state as InboundState
    );
  };

  const shouldDisplayCompleteInboundButton = (): boolean => {
    return (
      !isShipper &&
      skuContents &&
      isContainerDeliveryInProgress() &&
      warehouseProps.isInboundPackagingSet &&
      inbound.actualArrivalTime !== null
    );
  };

  return (
    <div id="container-delivery-summary">
      <div className="container-fluid">
        <div className="contents-container">
          <header>
            <div className="delivery-summary-header">
              <h2>Inbound Summary</h2>
              {isShipper && isContainerDeliveryActive() && (
                <a data-testid="add-inventories-button" className="add-documents">
                  <i className="fa fa-plus"></i> Add Inventory
                </a>
              )}
            </div>
          </header>
          {showQuantityWarning && [InboundState.Receiving, InboundState.Completed].includes(inboundState) && (
            <InfoBox info="Received quantities do not match expected quantities." infoType="warning" />
          )}
          <Tabs tabs={getTabs()} tabEvent={(tabKey) => onTabSelection(tabKey)} />
          {renderTable()}
        </div>
      </div>
      {shouldDisplayCompleteInboundButton() && (
        <CompleteInbound
          inbound={inbound}
          containersService={props.containersService}
          skuContents={skuContents}
          isBolUploaded={warehouseProps.isBolUploaded}
          reservation={props.reservation}
          handleErrors={warehouseProps.handleInboundCompletionErrors}
          reloadContainer={warehouseProps.reloadContainer}
        />
      )}
    </div>
  );
};
